/* =================================================================
   DiagView - Interactive Diagram Viewer
   ================================================================= */

/* Screen-reader only: visually hidden but accessible to assistive tech */
.diagview-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Theme Variables */
:root {
  --dv-bg: transparent;
  --dv-header-bg: rgba(128, 128, 128, 0.05);
  --dv-text-color: inherit;
  --dv-border-color: rgba(128, 128, 128, 0.2);
  --dv-accent: #3b82f6;
  /* Default Fallback */
  --dv-primary: var(--dv-accent);
  --dv-popover-bg: rgba(128, 128, 128, 0.08);
  --dv-btn-bg: rgba(128, 128, 128, 0.12);
  --dv-radius-sm: 8px;
  --dv-header-h: 52px;
}

@media (max-width: 639px) {
  :root {
    --dv-header-h: 48px;
  }
}

@media (prefers-color-scheme: dark) {
  :root {
    --dv-border-color: rgba(255, 255, 255, 0.15);
    --dv-header-bg: rgba(255, 255, 255, 0.05);
  }
}

/* Reset for isolation */
.diagview-wrapper *,
.diagview-modal *,
.diagview-menu * {
  text-decoration: none !important;
  box-sizing: border-box;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
}

/* Premium Focus Indicators */
.diagview-modal *:focus-visible,
.diagview-wrapper *:focus-visible,
.diagview-menu *:focus-visible {
  outline: none !important;
  box-shadow:
    0 0 0 2px var(--dv-bg),
    0 0 0 4px var(--dv-accent) !important;
  border-radius: inherit !important;
  transition: box-shadow 0.2s ease;
}

/* Specific override for Search Input (Wrapper handles the focus style) */
.diagview-search-input:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}

/* SVG Text Integrity */
.diagview-modal svg text,
.diagview-modal svg tspan,
.diagview-wrapper svg text,
.diagview-wrapper svg tspan {
  white-space: nowrap !important;
  word-wrap: normal !important;
  overflow-wrap: normal !important;
  text-overflow: clip !important;
}

/* Diagram Container */
.diagview-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  border: 1px solid var(--dv-border-color);
  border-radius: 10px;
  margin: 1.5rem 0;
  background: transparent;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  contain: layout style paint;
  content-visibility: auto;
  contain-intrinsic-size: auto 500px;
}

.diagview-wrapper:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-color: rgba(128, 128, 128, 0.4);
}

/* Viewport */
.diagview-viewport {
  width: 100%;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: zoom-in;
  overflow: hidden;
  padding: 24px;
  min-height: 200px;
  border-radius: 8px;
  user-select: none;
  background: rgba(128, 128, 128, 0.02);
  contain: layout style;
}

.diagview-viewport > * {
  width: 100% !important;
  display: flex;
  justify-content: center;
}

.diagview-viewport svg {
  width: 100% !important;
  height: auto !important;
  max-width: 100% !important;
  max-height: 80vh !important;
  display: block;
  transition: transform 0.2s ease;
  background: transparent !important;
}

/* Header Controls */
.diagview-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  width: 100%;
  background: var(--dv-header-bg);
  border-bottom: 1px solid var(--dv-border-color);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.diagview-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--dv-text-color);
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
}

.diagview-btn-group {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* Desktop: Hidden by default, slides down on hover */
@media (hover: hover) and (pointer: fine) {
  .diagview-controls {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-5px);
  }

  .diagview-wrapper:hover .diagview-controls,
  .diagview-wrapper:focus-within .diagview-controls {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
}

/* Mobile: Always visible */
@media (hover: none) or (pointer: coarse) {
  .diagview-controls {
    opacity: 1 !important;
    pointer-events: auto !important;
  }
}

/* Floating Layout (Low-Centered Static) */
.diagview-controls-floating {
  position: relative !important;
  margin-top: 10px;
  width: 100% !important;
  background: transparent !important;
  padding: 0 !important;
  display: flex;
  gap: 12px;
  align-items: center !important;
  justify-content: center;
  z-index: 10;
  opacity: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  transform: translateY(-5px);
}

.diagview-wrapper:hover .diagview-controls-floating {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

/* Floating Mode Overrides */
.diagview-controls-floating .diagview-label {
  display: none !important;
}

.diagview-controls-floating .diagview-btn {
  background: transparent !important;
  border: none !important;
  color: #fff !important;
  width: 36px;
  height: 36px;
  opacity: 1;
  box-shadow: none !important;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
}

.diagview-controls-floating .diagview-btn:hover {
  border-radius: 50%;
}

/* Action Buttons */
.diagview-btn {
  all: unset;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(128, 128, 128, 0.15);
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 8px;
  color: var(--dv-text-color);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0 !important;
  box-sizing: border-box !important;
  position: relative;
}

.diagview-btn:hover {
  background: rgba(128, 128, 128, 0.25);
  transform: translateY(-1px);
}

.diagview-btn:active {
  scale: 0.95;
}

.diagview-btn svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
  pointer-events: none;
}

/* Button Success State */
.diagview-btn.success {
  background: #10b981 !important;
  color: white !important;
}

.diagview-btn.success::after {
  content: "✓";
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  animation: checkmark 0.3s ease;
}

@keyframes checkmark {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }

  50% {
    opacity: 1;
    transform: scale(1.2);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Fullscreen Modal */
.diagview-modal {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 2147483647 !important; /* Maximum 32-bit integer to stay on top of everything */
  flex-direction: column;
  overflow: hidden;
  background-color: var(--dv-bg) !important;
  color: var(--dv-text-color);
  outline: none !important;
  /* Prevent native pinch-zoom on non-viewport areas (topbar, FAB, menu).
     Without this, pinch-zooming on UI chrome triggers browser zoom which
     persists after the modal closes — leaving the background page zoomed.
     The diagram viewport has touch-action:none so Panzoom is unaffected. */
  touch-action: pan-x pan-y;
}

.diagview-modal.open {
  display: flex;
  animation: dv-fade-in 0.2s ease-out;
}

@keyframes dv-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Modal Content Wrapper */
.diagview-modal-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
}

/* Topbar */
.diagview-topbar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  /* right padding reserves space for the always-absolute close btn */
  padding: 0 52px 0 8px;
  min-height: var(--dv-header-h);
  flex-shrink: 0;
  overflow: visible; /* allow tooltip overflow */
  background: var(--dv-header-bg);
  border-bottom: 1px solid var(--dv-border-color);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  /* Counter-scale when browser is pinch-zoomed so controls stay at design size.
     --dv-zoom-comp is set by viewport.js startVisualViewportSync(). */
  zoom: var(--dv-zoom-comp, 1);
}

@media (min-width: 640px) {
  .diagview-topbar {
    padding: 0 60px 0 20px; /* right: 60px = room for absolute close btn only */
    justify-content: center;
    overflow: visible;
  }
}

/* ── Mobile icon action row ────────────────────────────── */
.dv-topbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

@media (min-width: 640px) {
  .dv-topbar-actions {
    display: none; /* desktop uses full search + dedicated btn */
  }
}

/* Generic 36×36 icon button (search, text-select, etc.) */
.dv-icon-btn {
  all: unset;
  box-sizing: border-box;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid var(--dv-border-color) !important;
  background: rgba(128, 128, 128, 0.06);
  color: var(--dv-text-color);
  transition:
    background 0.2s,
    color 0.2s,
    border-color 0.2s;
}
.dv-icon-btn svg {
  width: 16px;
  height: 16px;
  stroke: currentColor;
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  pointer-events: none;
}
.dv-icon-btn:hover {
  background: rgba(128, 128, 128, 0.14);
  color: var(--dv-text-color);
}
.dv-icon-btn.active {
  color: var(--dv-accent, #3b82f6);
  border-color: var(--dv-accent, #3b82f6) !important;
  background: rgba(59, 130, 246, 0.1);
}
.dv-icon-btn:focus-visible {
  outline: 2px solid var(--dv-accent);
  outline-offset: 2px;
}

/* ── Mobile: search container slides in-place on topbar ── */
/* On mobile the .diagview-search-container is normally hidden.          */
/* Tapping the search icon adds .search-open to the topbar, which slides */
/* the real search input in from the left — no duplicate DOM needed.     */
@media (max-width: 639px) {
  .diagview-search-container {
    display: flex;
    flex: 0;
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
    padding: 0;
    transition:
      max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.2s ease,
      flex 0.25s ease;
  }

  .diagview-topbar.search-open .diagview-search-container {
    flex: 1;
    max-width: 600px;
    opacity: 1;
    pointer-events: auto;
    overflow: visible;
  }

  /* Show back button and hide search icon inside wrapper */
  .diagview-topbar.search-open .diagview-search-back {
    display: flex;
  }
  .diagview-topbar.search-open .diagview-search-icon {
    display: none;
  }

  /* Hide the action row and branding when search is expanded */
  .diagview-topbar.search-open .dv-topbar-actions,
  .diagview-topbar.search-open .diagview-branding {
    display: none !important;
  }
}

/* When search is open, hide specific buttons marked with this class */
.diagview-topbar.search-open .dv-hide-on-search {
  display: none;
}

/* ── Desktop text-select button — in-flow flex item after shortcut hint ─ */
/* Sits in the natural flex row so it never overlaps centered content.      */
.dv-text-select-btn {
  display: none; /* mobile: hidden */
}

@media (min-width: 640px) {
  .dv-text-select-btn {
    all: unset;
    box-sizing: border-box;
    position: relative; /* required: tooltip ::after is position:absolute */
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    cursor: pointer;
    margin-left: 8px; /* breathing room after shortcut hint */
    border: 1px solid var(--dv-border-color) !important;
    background: rgba(128, 128, 128, 0.06);
    color: var(--dv-text-color);
    transition:
      background 0.2s,
      color 0.2s,
      border-color 0.2s;
  }
  .dv-text-select-btn svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    pointer-events: none;
  }
  .dv-text-select-btn:hover {
    background: rgba(59, 130, 246, 0.1);
    color: var(--dv-accent, #3b82f6);
    border-color: var(--dv-accent, #3b82f6) !important;
  }
  .dv-text-select-btn.active {
    color: #ffffff !important;
    border-color: var(--dv-accent, #3b82f6) !important;
    background: var(--dv-accent, #3b82f6) !important;
    box-shadow: 0 0 12px rgba(59, 130, 246, 0.4);
  }
  .dv-text-select-btn:focus-visible {
    outline: 2px solid var(--dv-accent);
    outline-offset: 2px;
  }
}

/* ── CSS tooltip (desktop only) ───────────────────────── */
/* Usage: data-tooltip="Your message here" on any element  */
@media (min-width: 640px) {
  [data-tooltip] {
    position: relative;
  }
  [data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%) scale(0.94);
    background: #1e293b;
    color: #e2e8f0;
    padding: 5px 10px;
    border-radius: 7px;
    font-size: 11.5px;
    font-family:
      system-ui,
      -apple-system,
      sans-serif;
    font-weight: 500;
    white-space: nowrap;
    letter-spacing: 0.01em;
    pointer-events: none;
    opacity: 0;
    transition:
      opacity 0.15s ease,
      transform 0.15s ease;
    z-index: 1000020;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.07);
  }
  [data-tooltip]:hover::after {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }
}

/* Branding Link - Topbar (Desktop Focus) */
.diagview-branding {
  font-size: 13px;
  font-weight: 800;
  color: var(--dv-text-color);
  text-decoration: none !important;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  white-space: nowrap;
  letter-spacing: 0.05em;
  margin-right: 15px;
  display: none;
}

/* Reactive Toggle Class */
.dv-hide-branding .diagview-branding,
.dv-hide-branding .dv-menu-brand,
.dv-hide-branding .dv-menu-footer {
  display: none !important;
}

@media (min-width: 320px) {
  .diagview-branding {
    display: block;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    pointer-events: none;
  }
  .diagview-branding * {
    pointer-events: auto;
  }
}

@media (min-width: 640px) {
  .diagview-branding {
    display: block;
    position: static;
    transform: none;
    margin-right: 15px;
    pointer-events: auto;
  }
}

.diagview-branding:hover {
  opacity: 1;
  color: var(--dv-primary, var(--dv-accent, #3b82f6));
}

/* Branding Footer - Menu Panel (Mobile/Desktop) */
.dv-menu-footer {
  margin-top: 12px;
  padding: 12px 0 4px 0;
  border-top: 1px solid rgba(128, 128, 128, 0.1);
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--dv-text-color);
  opacity: 0.75;
  letter-spacing: 0.03em;
  line-height: 1.6;
}

.dv-menu-footer a {
  color: inherit;
  text-decoration: none !important;
  font-weight: 700;
  transition:
    color 0.2s ease,
    opacity 0.2s ease;
  display: inline-block; /* Ensure no absolute positioning leaks */
  position: relative;
}

.dv-menu-brand {
  color: var(--dv-primary, var(--dv-accent, #3b82f6)) !important;
  opacity: 1 !important;
}

.dv-menu-footer a:hover {
  color: var(--dv-primary, var(--dv-accent, #3b82f6));
  opacity: 1;
}

.dv-menu-footer:hover {
  opacity: 0.8;
}

/* Search Container */
.diagview-search-container {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  max-width: 600px;
  min-width: 0;
  overflow: hidden;
}

.diagview-search-wrapper {
  flex: 1;
  display: flex;
  gap: 10px;
  padding: 0 14px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 12px;
  height: 38px;
  align-items: center;
  background: rgba(128, 128, 128, 0.06);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.diagview-search-wrapper:has(.diagview-search-input:focus-visible) {
  border-color: var(--dv-accent);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  background: var(--dv-bg);
}

/* Subtle border for active interaction (no shadow box) */
.diagview-search-wrapper:focus-within {
  border-color: var(--dv-accent);
  background: var(--dv-bg);
}

.diagview-search-icon {
  opacity: 0.5;
  flex-shrink: 0;
  color: var(--dv-text-color);
}

.diagview-search-input {
  appearance: none;
  background: transparent;
  border: none;
  outline: none;
  flex: 1;
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 0 6px;
  margin: 0;
  font-size: 14px;
  line-height: normal;
  color: var(--dv-text-color);
  box-sizing: border-box;
  border-radius: 0;
}

.diagview-search-input:focus::placeholder {
  color: transparent;
}

.diagview-search-clear {
  all: unset;
  cursor: pointer;
  font-size: 14px;
  opacity: 0;
  padding: 4px;
  color: var(--dv-text-color);
  line-height: 1;
  transition: opacity 0.2s;
}

.diagview-search-clear.show {
  opacity: 0.5;
}

.diagview-search-clear:hover {
  opacity: 1;
  color: #ff4444;
}

/* Zoom Display */
.diagview-zoom-display {
  display: none;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  padding: 4px 10px;
  background: var(--dv-header-bg);
  border: 1px solid var(--dv-border-color);
  border-radius: 6px;
  color: var(--dv-text-color);
  opacity: 0.9;
  white-space: nowrap;
  min-width: 52px;
  text-align: center;
}

.diagview-shortcut-hint {
  display: none;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  opacity: 0.6;
  color: var(--dv-text-color);
  white-space: nowrap;
  flex-shrink: 0;
}

.diagview-shortcut-hint kbd {
  padding: 2px 6px;
  background: var(--dv-header-bg);
  border-radius: 4px;
  font-family: ui-monospace, monospace;
  font-weight: 600;
  border: 1px solid var(--dv-border-color) !important;
}

@media (min-width: 640px) {
  .diagview-zoom-display {
    display: block;
  }

  .diagview-shortcut-hint {
    display: flex;
  }
}

/* Close Button — always pinned top-right */
.diagview-close-btn {
  all: unset;
  box-sizing: border-box;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 300;
  border: 1px solid var(--dv-border-color) !important;
  background: rgba(128, 128, 128, 0.08);
  color: var(--dv-text-color);
  transition:
    background 0.2s,
    color 0.2s,
    border-color 0.2s,
    transform 0.2s;
}

.diagview-close-btn:hover {
  background: rgba(255, 68, 68, 0.15);
  color: #ff4444;
  border-color: rgba(255, 68, 68, 0.3) !important;
  transform: translateY(-50%) scale(1.06);
}

.diagview-close-btn:active {
  transform: translateY(-50%) scale(0.94);
}

.diagview-close-btn:focus-visible {
  outline: 2px solid var(--dv-accent);
  outline-offset: 2px;
}

/* Search Highlighting - Mermaid Optimized */
.dv-searching .node,
.dv-searching .cluster,
.dv-searching .edgePath,
.dv-searching .label,
.dv-searching text {
  transition: opacity 0.2s ease;
  will-change: opacity;
}

/*
 * WCAG Contrast Exception — intentional design decision (audit item B5)
 *
 * Non-matching nodes are dimmed to opacity: 0.15 during active search.
 * This technically violates WCAG 2.1 SC 1.4.3 (minimum contrast ratio 4.5:1)
 * for those nodes.
 *
 * Rationale for exception:
 * 1. The dimming is TRANSIENT — it only occurs while the user is actively typing
 *    in the search box and disappears the moment search is cleared.
 * 2. The PRIMARY information ("these nodes don't match") is conveyed by the dimming
 *    itself, not by reading the dimmed text — the content of dimmed nodes is
 *    intentionally de-emphasized, not the accessible channel.
 * 3. Matching nodes remain at full opacity (see .dv-search-match below) and are
 *    announced via the aria-live region (#diagview-search-status).
 * 4. This pattern matches established design systems (VS Code search, GitHub search)
 *    that dim non-matching items without WCAG exception.
 *
 * Screen reader users get match counts via aria-live="polite" — they do not
 * rely on the visual opacity state to understand search results.
 */
.dv-searching .node:not(.dv-search-match),
.dv-searching .cluster:not(.dv-search-match),
.dv-searching .label:not(.dv-search-match),
.dv-searching text:not(.dv-search-match) {
  opacity: 0.15 !important;
  pointer-events: none !important;
}

/* Match Highlighting - Premium Glow + Pulse */
.dv-searching .dv-search-match {
  opacity: 1 !important;
  filter: drop-shadow(0 0 12px var(--dv-accent)) !important;
  animation: dv-pulse 2s infinite ease-in-out;
}

/* Ensure text inside matches remains crisp and original */
.dv-searching .dv-search-match text,
.dv-searching text.dv-search-match {
  fill: inherit !important;
  stroke: none !important;
  font-weight: inherit !important;
}

/* Pulse animation for easy discovery */
@keyframes dv-pulse {
  0% {
    filter: drop-shadow(0 0 8px var(--dv-accent));
  }
  50% {
    filter: drop-shadow(0 0 16px var(--dv-accent));
    brightness: 1.1;
  }
  100% {
    filter: drop-shadow(0 0 8px var(--dv-accent));
  }
}

/* Specific fix for edge paths (lines) */
.dv-searching .edgePath.dv-search-match path {
  stroke: var(--dv-accent) !important;
  stroke-width: 3px !important;
  opacity: 1 !important;
}

/* Modal Viewport - Diagram Container */
.diagview-modal-viewport {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: grab;
  background: var(--dv-bg);
  touch-action: none;
  user-select: none;
  position: relative;
  min-height: 0;
  contain: layout;
}

.diagview-modal-viewport:active {
  cursor: grabbing;
}

/* Text-select mode: overrides user-select:none so SVG labels are copyable */
.diagview-modal-viewport.dv-text-select {
  user-select: text;
  cursor: text;
}
.diagview-modal-viewport.dv-text-select:active {
  cursor: text;
}

/* Force text-selectable on SVG text nodes — user-select doesn't reliably
   cascade into SVG internals without !important across all browsers.
   Also reset pointer-events so the OS can deliver mouse events for selection. */
.diagview-modal-viewport.dv-text-select svg,
.diagview-modal-viewport.dv-text-select svg text,
.diagview-modal-viewport.dv-text-select svg tspan,
.diagview-modal-viewport.dv-text-select svg foreignObject,
.diagview-modal-viewport.dv-text-select svg foreignObject * {
  user-select: text !important;
  -webkit-user-select: text !important;
  pointer-events: auto !important;
  cursor: text !important;
}

/* Global helper to ensure selection is allowed */
.dv-selection-allowed {
  user-select: text !important;
  -webkit-user-select: text !important;
}

.diagview-modal-viewport svg {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  transform-origin: center center !important;
}

/* Floating Menu Container */
.diagview-fab-container {
  position: fixed;
  bottom: clamp(12px, 3vh, 24px);
  right: clamp(12px, 3vw, 24px);
  z-index: 1000010;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  pointer-events: none;
  /* Counter-scale when browser is pinch-zoomed so controls stay at design size.
     --dv-zoom-comp is set by viewport.js startVisualViewportSync(). */
  zoom: var(--dv-zoom-comp, 1);
}

.diagview-fab-container > * {
  pointer-events: auto;
}

/* FAB Button */
.diagview-fab-btn {
  all: unset;
  width: clamp(44px, 8vw, 56px);
  height: clamp(44px, 8vw, 56px);
  background: var(--dv-accent);
  color: white !important;
  border-radius: 16px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  cursor: pointer !important;
  pointer-events: auto !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2147483647;
}

.diagview-fab-btn:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.diagview-fab-btn:active {
  transform: scale(0.95);
}

.diagview-fab-btn.open {
  border-radius: 50%;
  transform: rotate(90deg);
  background: var(--dv-bg);
  color: var(--dv-text-color) !important;
  border: 1px solid var(--dv-border-color) !important;
}

.diagview-fab-btn svg {
  width: 24px;
  height: 24px;
  stroke: currentColor;
  stroke-width: 2.5;
  fill: none;
  pointer-events: none;
}

/* Floating Menu */
.diagview-menu {
  position: absolute;
  bottom: 70px;
  right: 0;
  width: min(260px, calc(100vw - 80px));
  max-height: min(70vh, calc(100vh - 120px));
  border-radius: 20px;
  box-shadow:
    0 24px 48px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px) scale(0.95);
  transform-origin: bottom right;
  /* MIN-4: Optimize transition to prevent GPU ghosting. 
     Transition only specific properties and use faster timing for snappiness. */
  transition:
    opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    visibility 0.15s step-end;
  will-change: transform, opacity;
  /* Clip content while closed/animating to prevent visual overflow */
  overflow: hidden;
  /* Solid fallback background that covers the full scrollable area.
     The ::before pseudo only covers the scroll viewport (inset:0),
     so without this, content that scrolls past the viewport has no background. */
  background-color: var(--dv-bg);
}

/* MIN-4: Move backdrop-filter to a pseudo-element to ensure perfect 
   synchronization between the blur and the content during opacity transitions. */
.diagview-menu::before {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--dv-bg) !important;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  z-index: -1;
  border-radius: inherit;
}

.diagview-menu.active {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
  transition:
    opacity 0.2s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.2s cubic-bezier(0.16, 1, 0.3, 1),
    visibility 0s linear;
  /* Restore scrolling when open — critical for heavy zoom levels where
     the menu content exceeds the viewport height */
  overflow-y: auto;
}

.dv-menu-sec {
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(128, 128, 128, 0.15);
}

.dv-menu-lbl {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.625rem;
  font-size: 0.625rem;
  font-weight: 800;
  color: var(--dv-text-color);
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  padding-left: 4px;
}

/* Zoom Controls (Row) */
.dv-zoom {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem;
  background: var(--dv-popover-bg);
  border-radius: var(--dv-radius-sm);
  border: 1px solid var(--dv-border-color);
}

.dv-zoom-val {
  flex: 1;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--dv-text-color);
  font-feature-settings: "tnum";
}

.dv-zoom button {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dv-btn-bg);
  border-radius: 8px;
  color: var(--dv-text-color);
  font-size: 1.25rem;
  cursor: pointer;
  /* MIN-4: Remove 'all' transition to stop buttons from lagging behind parent opacity */
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
  border: none;
}

.dv-zoom button svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
  stroke: currentColor;
  fill: none;
}

.dv-zoom button:hover {
  background: var(--dv-primary, var(--dv-accent, #3b82f6));
  color: #fff;
  transform: translateY(-1px);
}

/* Export Grid (3 columns for compact look) */
.dv-exp {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.375rem;
}

.dv-exp button {
  min-height: 32px;
  padding: 0.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 8px;
  color: var(--dv-text-color);
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  /* MIN-4: Remove 'all' transition to stop buttons from lagging behind parent opacity */
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-decoration: none !important;
}

.dv-exp button:hover {
  background: var(--dv-primary, var(--dv-accent, #3b82f6));
  border-color: transparent;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Export label row modifier (contains the transparent checkbox) */
.dv-menu-lbl--row {
  justify-content: space-between;
}

/* Transparent-export checkbox label */
.dv-exp-trans-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  cursor: pointer;
  text-transform: none;
  letter-spacing: normal;
}

/* Transparent-export checkbox input (Premium Custom Checkbox) */
.dv-exp-trans-chk {
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  width: 14px;
  height: 14px;
  border: 1.5px solid var(--dv-border-color);
  border-radius: 4px;
  background: rgba(128, 128, 128, 0.1);
  cursor: pointer;
  display: inline-grid;
  place-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-shrink: 0;
}

.dv-exp-trans-chk:checked {
  background: var(--dv-primary, var(--dv-accent, #3b82f6));
  border-color: var(--dv-primary, var(--dv-accent, #3b82f6));
}

.dv-exp-trans-chk:checked::before {
  content: "";
  width: 8px;
  height: 8px;
  transform: scale(1);
  background-color: #fff;
  clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
}

.dv-exp-trans-chk:hover {
  border-color: var(--dv-primary, var(--dv-accent, #3b82f6));
  background: rgba(128, 128, 128, 0.2);
}

.dv-exp-trans-chk:focus-visible {
  outline: 2px solid var(--dv-primary, var(--dv-accent, #3b82f6));
  outline-offset: 2px;
}

/* Transparent-export format hint "(PNG/WebP/SVG)" */
.dv-exp-trans-hint {
  opacity: 0.5;
  font-size: 8px;
}

/* Menu List Items (Tools) */
.dv-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 44px;
  padding: 0.625rem 0.875rem;
  background: transparent;
  border-radius: 12px;
  color: var(--dv-text-color);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 2px;
  border: none;
  text-decoration: none !important;
}

.dv-menu-item:hover {
  background: var(--dv-primary, var(--dv-accent, #3b82f6));
  color: white;
}

.dv-menu-item:hover svg {
  stroke: white;
}

.dv-menu-item.active {
  background: var(--dv-primary);
  color: white !important;
}

.dv-menu-item svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  flex-shrink: 0;
}

/* Minimap */
.diagview-minimap {
  position: fixed;
  left: clamp(12px, 3vw, 24px);
  bottom: clamp(12px, 3vh, 24px);
  width: min(160px, 40vw);
  height: min(100px, 25vh);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dv-bg);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid var(--dv-border-color) !important;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
  z-index: 1000005;
}

@media (max-width: 768px) {
  .diagview-minimap {
    display: none !important;
  }
}

.diagview-minimap.show {
  opacity: 1;
  visibility: visible;
}

.diagview-minimap svg {
  width: 100%;
  height: 100%;
  object-fit: contain;
  /* minimap text must never be selectable — it's too small to be useful */
  user-select: none;
  pointer-events: none;
}

.dv-mm-v {
  position: absolute;
  border: 2px solid var(--dv-accent, #3b82f6) !important;
  background: rgba(59, 130, 246, 0.1);
  pointer-events: none;
}

/* Laser Pointer (Meeting Mode) */
.diagview-laser {
  position: fixed;
  width: 28px;
  height: 28px;
  pointer-events: none;
  z-index: 1000015;
  transform: translate(-50%, -50%);
  display: none;
}

.diagview-laser::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, #ef4444 0%, transparent 70%);
  animation: dv-laser 1s ease-in-out infinite;
}

.diagview-laser::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 10px;
  background: #ef4444;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 16px 6px rgba(239, 68, 68, 0.6);
}

@keyframes dv-laser {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.7;
    transform: scale(1.3);
  }
}

.diagview-modal-viewport.meeting,
.diagview-modal-viewport.meeting * {
  cursor: none !important;
}

/* Toast Notifications */
.diagview-toast-container {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
  align-items: center;
  z-index: 2147483647; /* Match modal z-index (last child wins) */
  pointer-events: none;
  width: 90%;
  max-width: 500px;
  /* Counter-scale when browser is pinch-zoomed so toasts stay at design size.
     --dv-zoom-comp is set by viewport.js startVisualViewportSync(). */
  zoom: var(--dv-zoom-comp, 1);
}

.diagview-toast {
  padding: 12px 24px;
  border-radius: 12px;
  font: 600 14px system-ui;
  background: var(--dv-accent);
  color: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  white-space: normal;
  text-align: center;
  width: fit-content;
  max-width: 100%;
  line-height: 1.4;
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .diagview-modal *,
  .diagview-wrapper * {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

@media print {
  .diagview-controls,
  .diagview-menu,
  .diagview-modal {
    display: none !important;
  }
}

/* === Configurable Button Styles === */

/* Style: transparent */
.dv-btn-transparent {
  background: transparent !important;
  border: none !important;
  color: var(--dv-accent) !important;
  box-shadow: none !important;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
}

.dv-btn-transparent:hover {
  transform: scale(1.1);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

/* Style: accent (Icon Only) */
.dv-btn-accent {
  background: transparent !important;
  border: 1px solid color-mix(in srgb, var(--dv-accent) 40%, transparent) !important;
  color: var(--dv-accent) !important;
}

.dv-btn-accent svg {
  fill: var(--dv-accent) !important;
  stroke: var(--dv-accent) !important;
}

.dv-btn-accent:hover {
  background: color-mix(in srgb, var(--dv-accent) 10%, transparent) !important;
  color: var(--dv-accent) !important;
  border-color: var(--dv-accent) !important;
}

.dv-btn-accent:hover svg {
  fill: var(--dv-accent) !important;
  stroke: var(--dv-accent) !important;
}

/* Style: solid (High Contrast) */
.dv-btn-solid {
  background: var(--dv-accent) !important;
  color: #fff !important;
  border: 1px solid transparent !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.dv-btn-solid:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

/* Style: neutral (Classic/Grey) */
.dv-btn-neutral {
  background: rgba(128, 128, 128, 0.15) !important;
  border: 1px solid rgba(128, 128, 128, 0.2) !important;
  color: var(--dv-text-color) !important;
}

.dv-btn-neutral:hover {
  background: rgba(128, 128, 128, 0.25) !important;
  color: var(--dv-text-color) !important;
}

/* === Loading Indicator === */
.diagview-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dv-bg);
  z-index: 1000010;
  transition: opacity 0.3s ease;
}

.diagview-loading.hide {
  opacity: 0;
  pointer-events: none;
}

.diagview-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--dv-border-color);
  border-top-color: var(--dv-accent);
  border-radius: 50%;
  animation: dv-spin 0.8s linear infinite;
}

@keyframes dv-spin {
  to {
    transform: rotate(360deg);
  }
}

.diagview-search-back {
  display: none;
  background: none;
  border: none;
  padding: 4px 8px;
  color: var(--dv-text-color);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 8px;
  transition: background 0.2s;
}

.diagview-search-back:hover {
  background: rgba(128, 128, 128, 0.1);
}

/* === Error Boundary === */
.diagview-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: var(--dv-text-color);
  background: var(--dv-bg);
  border: 1px dashed var(--dv-border-color);
  border-radius: 12px;
  min-height: 120px;
}

.diagview-error-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 1rem;
  color: #ef4444;
}

.diagview-error-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #ef4444;
}

.diagview-error-message {
  font-size: 0.875rem;
  opacity: 0.7;
  max-width: 300px;
}

/* === Keyboard Help Modal === */
.diagview-help-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2147483648;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
}

.diagview-help-modal.show {
  opacity: 1;
  visibility: visible;
}

.diagview-help-content {
  background: var(--dv-bg);
  border: 1px solid var(--dv-border-color);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.3);
}

.diagview-help-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--dv-text-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.diagview-help-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--dv-text-color);
  opacity: 0.6;
  transition: opacity 0.2s;
}

.diagview-help-close:hover {
  opacity: 1;
}

.diagview-help-grid {
  display: grid;
  gap: 0.75rem;
}

.diagview-help-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(128, 128, 128, 0.35);
}

.diagview-help-row:last-child {
  border-bottom: none;
}

.diagview-help-key {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.diagview-help-key kbd {
  background: var(--dv-header-bg);
  border: 1px solid var(--dv-border-color);
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--dv-text-color);
}

.diagview-help-desc {
  font-size: 0.875rem;
  color: var(--dv-text-color);
  opacity: 0.8;
}

/* === Open Animation === */
.diagview-modal.animate-open .diagview-modal-content {
  animation: dv-modal-open 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes dv-modal-open {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Respect reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .diagview-modal.animate-open .diagview-modal-content {
    animation: none;
  }

  .diagview-spinner {
    animation-duration: 1.5s;
  }
}
